// Keept's plans as the app shows them: read straight from the server's catalogue, supabase/functions/_shared/plans.json
// (the @shared alias), so every limit is written once. Two plans, individual: one account is one person and one plan,
// priced per account; limits per calendar month and per day (New York time) — a Haiku pool, which counts every command, and an
// Opus pool, which counts the commands where Opus ran (such a command counts in both; Starter: Haiku only).
// The trial: 7 days, no card, 10 commands a day (New York time), Professional's routing, while billing is on
// (config.billing.enabled). With billing off there is nothing to buy: the server reports trialEnds: false — the 10 a
// day without an end (no days left, no plan to pick) — or, with its BILLING_OFF_TRIAL_CAP switch off,
// trialEnforced: false and no limits.
import catalogue from "@shared/plans.json";

export type PlanId = "starter" | "professional";
/** A plan as plans.json writes it ({haikuPerMonth}, {opusPerMonth}, {haikuPerDay} and {opusPerDay} in `what` are filled in by planWhat). */
export interface PlanSpec { name: string; monthly: number; yearly: number; haikuPerMonth: number; opusPerMonth: number; haikuPerDay: number; opusPerDay: number; escalationsPerMonth: number | null; what: string }
type NumberKey = "haikuPerMonth" | "opusPerMonth" | "haikuPerDay" | "opusPerDay";

const { $comment: _comment, ...rest } = catalogue as typeof catalogue & { $comment?: string };
void _comment;
export const CATALOGUE = rest as Omit<typeof catalogue, "$comment" | "plans" | "trialLimitsOf"> & { trialLimitsOf: PlanId; plans: Record<PlanId, PlanSpec> };
export const PLAN_IDS: PlanId[] = ["starter", "professional"];
/** How many QuickBooks companies (her clients) one account keeps connected at once, on every plan: the server's safety cap (connectionRule). */
export const COMPANIES_PER_ACCOUNT: number = (CATALOGUE as { companiesPerAccount?: number }).companiesPerAccount ?? 100;

const n = (x: number) => x.toLocaleString("en-US");
/** A plan's words with its numbers filled in (as the server's plans.ts does). */
export const planWhat = (p: PlanSpec): string => p.what.replace(/\{(haikuPerMonth|opusPerMonth|haikuPerDay|opusPerDay)\}/g, (_, k: NumberKey) => n(p[k]));

/** A plan's limits as short lines (the plan cards, the paywall): made from the numbers, never written twice. */
export function limitLines(p: PlanSpec): string[] {
  if (!p.opusPerMonth) return [`${n(p.haikuPerMonth)} commands a month, all on Haiku 5.5`, `Up to ${n(p.haikuPerDay)} commands a day`];
  return [`${n(p.haikuPerMonth)} commands a month, up to ${n(p.opusPerMonth)} on Opus 5.5`, `Up to ${n(p.haikuPerDay)} commands a day, ${n(p.opusPerDay)} on Opus 5.5`];
}

/** Under the plans, as usekeept.com/store says it: the limits are ceilings, and what a day fits depends on the work. */
export const CAPACITY_NOTE = "A command can cover one client or all of them; large commands across many clients use more of the day's capacity, so a busy day may fit fewer.";

/** The day's limit in one short line (Account → Today's usage): "N commands a day", or with the day's Opus pool ", M on Opus 5.5". */
export function dayLine(perDay: number, opusPerDay?: number | null): string {
  return `${n(perDay)} commands a day${opusPerDay ? `, ${n(opusPerDay)} on Opus 5.5` : ""}`;
}

/** What one month or year of a plan costs: the plan's price, per account. */
export function priceOf(id: PlanId, period: "monthly" | "yearly" = "monthly"): number {
  const p = CATALOGUE.plans[id];
  return period === "yearly" ? p.yearly : p.monthly;
}

export const money = (n: number) => n.toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Professional's model setting: the router decides, or every command on one model. */
export type ModelChoice = "auto" | "haiku" | "opus";

/** The account's month, as the server counts it (runner `usage`, and the `usage` event of every command). */
export interface MonthUsage {
  plan: PlanId; planName: string; trial: boolean; trialEndsAt: string | null; trialOver: boolean;
  /** The trial's limits (10 a day) apply: false on the trial only while billing is off and the server's cap is switched off. */
  trialEnforced?: boolean;
  /** The trial can end and a plan can be bought: false while billing is off (10 a day, no end, nothing to buy). */
  trialEnds?: boolean;
  /** Every command this month and the plan's commands a month (the Haiku pool; null on the trial). */
  commands: number; commandsLimit: number | null;
  /** The Haiku pool this month: every command, Opus ones too (null limit on the trial). */
  haiku?: number; haikuLimit?: number | null; haikuLeft?: boolean;
  opus: number; opusLimit: number;
  /** Today (New York time) on a plan: commands and the day's limit, Opus commands and the day's Opus pool (null on the trial). */
  today?: number | null; todayLimit?: number | null; opusToday?: number | null; opusDayLimit?: number | null;
  /** Today's share of the day's command limit, a whole percent (null without a daily limit); when the day resets. */
  dayPercent?: number | null; dayResetsAt?: string;
  /** At or past 80 % of a day's pool. */
  dayAlert?: boolean;
  /** Starter's automatic Opus escalations this month and its cap (0: none; null on the other plans). */
  escalations: number; escalationsLimit: number | null; escalationsLeft: boolean;
  /** The trial: commands today (New York time), the day's limit, days left, when the day resets (null off the trial). */
  trialToday: number | null; trialDayLimit: number | null; trialDaysLeft: number | null; trialDayResetsAt: string | null;
  alert: boolean; blocked: boolean; blockReason: "commands" | "day" | "trial_ended" | "trial_day" | null; writesAllowed: boolean;
  opusLeft: boolean;
  /** The account may pick its model (Professional, not the trial). */
  modelChoice?: boolean;
  resetsAt: string; alertMessage: string | null;
}

/** The pool that applies now: the trial's day; else the fuller of the Haiku and Opus pools (Starter: its commands). */
export function poolOf(u: MonthUsage): { pool: "trial" | "haiku" | "opus" | null; used: number; limit: number | null } {
  if (u.trial) return { pool: "trial", used: u.trialToday ?? 0, limit: u.trialDayLimit };
  // An older server sends no haiku pool: its commands limit stands in.
  const haikuLimit = u.haikuLimit ?? u.commandsLimit ?? null;
  const haiku = u.haiku ?? u.commands;
  const hs = haikuLimit ? haiku / haikuLimit : -1, os = u.opusLimit ? u.opus / u.opusLimit : -1;
  if (hs < 0 && os < 0) return { pool: null, used: u.commands, limit: null };
  return os > hs ? { pool: "opus", used: u.opus, limit: u.opusLimit } : { pool: "haiku", used: haiku, limit: haikuLimit };
}

/** The share of the pool that applies (the trial: today's commands; else the fuller pool), 0–1, or null without a limit. */
export function shareOf(u: MonthUsage): number | null {
  if (u.trial) return u.trialOver ? 1 : u.trialDayLimit ? Math.min(1, (u.trialToday ?? 0) / u.trialDayLimit) : null;
  const p = poolOf(u);
  return p.limit ? Math.min(1, p.used / p.limit) : null;
}
